import { venueCategories, type VenueCategory } from "../domain/types.js";

export interface RoutedState {
  cityId?: string;
  date?: string;
  time?: string;
  categories: Set<VenueCategory>;
  showCoverage: boolean;
  legendOpen: boolean;
  controlsOpen: boolean;
  selectedCellH3?: string;
  selectedVenueId?: string;
}

interface SerializableState {
  cityId: string;
  date: string;
  time: string;
  categories: ReadonlySet<VenueCategory>;
  showCoverage: boolean;
  legendOpen: boolean;
  controlsOpen: boolean;
  selectedCellH3?: string;
  selectedVenueId?: string;
}

const routedKeys = [
  "city",
  "date",
  "time",
  "categories",
  "coverage",
  "legend",
  "controls",
  "cell",
  "venue",
] as const;

const categorySet = new Set<string>(venueCategories);

function optionalValue(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value ? value.slice(0, 200) : undefined;
}

export function readRoutedState(search: string): RoutedState {
  const params = new URLSearchParams(search);
  const requestedCategories = optionalValue(params, "categories")
    ?.split(",")
    .filter((category): category is VenueCategory => categorySet.has(category));
  const categories = requestedCategories?.length
    ? new Set(requestedCategories)
    : new Set(venueCategories);
  const selectedVenueId = optionalValue(params, "venue");

  return {
    cityId: optionalValue(params, "city"),
    date: optionalValue(params, "date"),
    time: optionalValue(params, "time"),
    categories,
    showCoverage: params.get("coverage") === "1",
    legendOpen: params.get("legend") === "1",
    controlsOpen: params.get("controls") === "1",
    selectedCellH3: selectedVenueId ? undefined : optionalValue(params, "cell"),
    selectedVenueId,
  };
}

export function queryStringForState(currentSearch: string, state: SerializableState): string {
  const params = new URLSearchParams(currentSearch);
  routedKeys.forEach((key) => params.delete(key));

  params.set("city", state.cityId);
  params.set("date", state.date);
  params.set("time", state.time);

  const selectedCategories = venueCategories.filter((category) => state.categories.has(category));
  if (selectedCategories.length !== venueCategories.length) {
    params.set("categories", selectedCategories.join(","));
  }
  if (state.showCoverage) params.set("coverage", "1");
  if (state.legendOpen) params.set("legend", "1");
  if (state.controlsOpen) params.set("controls", "1");
  if (state.selectedVenueId) params.set("venue", state.selectedVenueId);
  else if (state.selectedCellH3) params.set("cell", state.selectedCellH3);

  const query = params.toString();
  return query ? `?${query}` : "";
}
